import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import Avatar from '@atlaskit/avatar';
import Form from '@atlaskit/form';
import { Box, Inline, xcss, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	errorFlags,
	FlagRenderer,
	useFlagService,
	type FlagConfiguration,
} from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireTrackAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { useProject_DEPRECATED_DO_NOT_USE } from '@atlassian/jira-router-resources-business-project-details/src/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { FORM_VALIDATION_ERROR, PACKAGE_NAME } from '../../../common/constants.tsx';
import AsyncDescriptionReadView from '../../../common/ui/description-read/index.tsx';
import { SuccessView } from '../../../common/ui/empty-views/index.tsx';
import { TitleWrapperTruncated } from '../../../common/ui/form-header/styled.tsx';
import { getFieldTypeConfig } from '../../../common/utils/field-config/index.tsx';
import { useJWMFormsFeatures } from '../../../controllers/features-context/index.tsx';
import { useFormSubmission } from '../../../controllers/form-submission/index.tsx';
import { useMediaPermissions } from '../../../controllers/media-upload-permissions/index.tsx';
import { useCreateIssue } from '../../../services/create-issue/index.tsx';
import {
	JiraCreateIssueByPublicFormRecaptchaType,
	type FormInputData,
} from '../../../services/create-issue/types.tsx';
import type { JiraBusinessFormWithFieldDataField } from '../../../services/get-form/types.tsx';
import { useUploadPermissions } from '../../../services/media-upload-permissions/index.tsx';
import { getFormAttachmentUploadUrl } from '../../../services/media-upload-permissions/utils.tsx';
import { FormAccessLevelTypes } from '../../../common/types.tsx';
import Condition from './condition/index.tsx';
import messages from './messages.tsx';
import ScreenreaderAnnouncer from './screenreader-announcer/index.tsx';
import { generateFieldAnalytics, useIntersectionObserver } from './utils.tsx';
import { Footer } from './footer/index.tsx';
import type { PublicFormRecaptchaResult } from './public-form-recaptcha/index.tsx';
import { Fields } from './fields/index.tsx';

const submissionErrorFlag = (errorMessage: string): FlagConfiguration => {
	if (errorMessage === FORM_VALIDATION_ERROR.ATTACHMENT_ERROR) {
		return {
			type: 'error',
			title: messages.submissionErrorFlag,
			description: messages.submissionAttachmentErrorContent,
			testId: 'business-form.ui.form-submission.fill-in-view.form-attachment-error-flag',
			messageId:
				'business-form.ui.form-submission.fill-in-view.flag.error.error.form-attachment-error-flag',
			messageType: 'transactional',
		};
	}
	return {
		type: 'error',
		title: messages.submissionErrorFlag,
		description: errorMessage,
		testId: 'business-form.ui.form-submission.fill-in-view.error-flag',
		messageId: 'business-form.ui.form-submission.fill-in-view.flag.error',
		messageType: 'transactional',
	};
};

const interactions = ['scroll', 'click', 'focus', 'keydown'];
const hasInitialInteractionCapture = true;

export const FillInView = () => {
	const [uploading, setUploading] = useState<boolean>(false);
	const [shouldRenderExtraFields, setShouldRenderExtraFields] = useState<boolean>(false);
	const [isVisible, setIsVisible] = useState<boolean>(true);
	const [recaptcha, setRecaptcha] = useState<PublicFormRecaptchaResult | undefined>();
	const [checkboxRecaptchaToken, setCheckboxRecaptchaToken] = useState<string | null | undefined>();
	const [isCheckboxRecaptchaNotVerifiedOnSubmit, setIsCheckboxRecaptchaNotVerifiedOnSubmit] =
		useState<boolean>(false);
	const [isInvisibleRecaptchaFailed, setIsInvisibleRecaptchaFailed] = useState<boolean>(false);
	const [isRecaptchaError, setIsRecaptchaError] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: formSubmissionData, formId } = useFormSubmission();
	const { data: projectData } = useProject_DEPRECATED_DO_NOT_USE();
	const { shouldHidePoweredBy } = useJWMFormsFeatures();
	const { formWithFieldData, fieldMetaData } = formSubmissionData ?? {};
	const { description, projectId, accessLevel, issueType } = formWithFieldData ?? {};
	const emailRequired =
		formWithFieldData && 'emailRequired' in formWithFieldData
			? formWithFieldData.emailRequired
			: undefined;
	const { showFlag } = useFlagService();
	const isAnonymous = useIsAnonymous();
	const isPublicForm =
		fg('jira-forms-public-access') && accessLevel === FormAccessLevelTypes.PUBLIC;

	// This is for description field
	const { uploadPermissions, fetchUploadPermissions, uploadPermissionStatus } =
		useUploadPermissions(true);
	const { mediaUploadPermissions, mediaUploadPermissionStatus, mediaContext } =
		useMediaPermissions();
	const isUploadPermissionNotReady =
		(!fg('fix_attachments_for_description_on_forms') || !isPublicForm) &&
		(uploadPermissionStatus !== 'COMPLETED' || mediaUploadPermissionStatus !== 'COMPLETED');

	const parsedDescription = useMemo(() => {
		if (description === undefined || description === '') {
			return undefined;
		}
		try {
			return JSON.parse(description);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (e: any) {
			log.safeErrorWithoutCustomerData(
				PACKAGE_NAME,
				'failed to parse form description in form submission',
			);
			// ignore description from backend if it's not a valid JSON format
			return undefined;
		}
	}, [description]);

	const analyticAttributes = useMemo(
		() => ({
			accessLevel: accessLevel?.toString(),
			isOutsideUser: !projectData || !projectData?.permissions.createIssues,
			formId,
			isLoggedIn: fg('jira-forms-public-access') ? !isAnonymous : undefined,
		}),
		[accessLevel, projectData, formId, isAnonymous],
	);

	const handleCreateIssueOnComplete = useCallback(() => {
		setIsVisible(false);
	}, [setIsVisible]);

	const handleOnRecaptchaLowScoreError = useCallback(() => {
		if (isInvisibleRecaptchaFailed) {
			// for checkbox recaptcha low score case
			// if invisible recaptcha has previously failed, this means checkbox recaptcha has also failed, thus prompt user to refresh.
			showFlag(errorFlags.genericError());
		} else {
			// for invisible recaptcha low score case
			// if invisible recaptcha has not failed, use this to trigger checkbox flow.
			setIsInvisibleRecaptchaFailed(true);
		}
	}, [setIsInvisibleRecaptchaFailed, isInvisibleRecaptchaFailed, showFlag]);

	const handleResetClick = useCallback(() => {
		setIsVisible(true);
	}, [setIsVisible]);

	const renderExtraFields = useCallback(() => {
		setShouldRenderExtraFields(true);
		if (typeof document !== 'undefined') {
			interactions.forEach((evt) =>
				document.removeEventListener(evt, renderExtraFields, hasInitialInteractionCapture),
			);
		}
	}, []);
	const setRef = useIntersectionObserver(renderExtraFields);
	const setIntersectionRef = useCallback((element: Element | null) => setRef(element), [setRef]);

	const {
		create,
		data: createIssueData,
		error: createIssueError,
		errorMessage,
		loading: createIssueLoading,
	} = useCreateIssue(
		formSubmissionData?.formWithFieldData,
		handleCreateIssueOnComplete,
		handleOnRecaptchaLowScoreError,
		projectData?.type,
		fg('jira-forms-public-access') ? analyticAttributes : undefined,
	);

	const handleInternalFormSubmitClick = useCallback(
		async (formData: FormInputData) => {
			fireUIAnalytics(
				createAnalyticsEvent({}),
				'form submitted',
				'formSubmission',
				analyticAttributes,
			);
			await create(formData, '', undefined);
		},
		[create, createAnalyticsEvent, analyticAttributes],
	);

	const handlePublicFormSubmitClick = useCallback(
		async (formData: FormInputData) => {
			if (isInvisibleRecaptchaFailed) {
				if (checkboxRecaptchaToken) {
					await create(
						formData,
						checkboxRecaptchaToken,
						JiraCreateIssueByPublicFormRecaptchaType.V2_CHECKBOX,
					);
					setIsInvisibleRecaptchaFailed(false);
					fireUIAnalytics(
						createAnalyticsEvent({}),
						'form submitted',
						'formSubmission',
						analyticAttributes,
					);
				} else {
					// no checkbox token available, either challenge not completed yet, or challenge expired.
					setIsCheckboxRecaptchaNotVerifiedOnSubmit(true);
				}

				return;
			}
			const recaptchaToken = await recaptcha?.getToken?.();

			// In public form, do not submit form if recaptcha token is not available
			setIsRecaptchaError(false);
			if (recaptchaToken) {
				await create(formData, recaptchaToken, JiraCreateIssueByPublicFormRecaptchaType.V3_SCORE);
				// if recaptcha fails due to user being detected as a bot, try again with checkbox
				fireUIAnalytics(
					createAnalyticsEvent({}),
					'form submitted',
					'formSubmission',
					analyticAttributes,
				);
			} else {
				setIsRecaptchaError(true);
			}
		},
		[
			create,
			createAnalyticsEvent,
			analyticAttributes,
			recaptcha,
			checkboxRecaptchaToken,
			isInvisibleRecaptchaFailed,
		],
	);

	const renderFields = useCallback(
		(fields: JiraBusinessFormWithFieldDataField[]) => {
			const renderedFields = fields.map((f) => {
				// If the field no longer exists, do not render it.
				if (!fieldMetaData || !fieldMetaData[f.fieldId]) {
					return null;
				}

				const fieldConfig = getFieldTypeConfig(f.type, projectData?.type);

				if (!fieldConfig) {
					fireOperationalAnalytics(createAnalyticsEvent({}), 'field missing', {
						type: f.type,
					});
					return null;
				}

				const {
					autoCompleteUrl,
					allowedValues = [],
					defaultFieldValue,
					schema,
				} = fieldMetaData[f.fieldId];
				/**
				 * autoCompleteUrls contains origin/host.
				 * In storybook, we want these to point to our proxy.
				 */
				let completeUrl = autoCompleteUrl;
				if (Boolean(process.env.STORYBOOK_JIRA_URL) && typeof autoCompleteUrl === 'string') {
					const url = new URL(autoCompleteUrl);
					completeUrl = `${url.pathname}${url.search}`;
				}

				const isFieldRequired = f.isRequired || !!f.isRequiredByForm;
				const fieldDescription = f.description;

				const field = (
					<fieldConfig.Component
						key={f.fieldId}
						fieldName={typeof f.alias === 'string' && f.alias.length > 0 ? f.alias : f.name}
						fieldId={f.fieldId}
						isRequired={isFieldRequired}
						description={fieldDescription}
						autoCompleteUrl={completeUrl}
						allowedValues={allowedValues}
						mediaUploadContexts={{
							uploadContext: uploadPermissions,
							attachmentUploadContext: mediaUploadPermissions,
						}}
						projectId={String(projectId)}
						issueTypeId={issueType?.id}
						projectKey={projectData?.key}
						onInProgressChange={setUploading}
						defaultValue={defaultFieldValue}
						attachmentCustomUploadURL={getFormAttachmentUploadUrl(formId)}
						canUserViewProject={Boolean(projectData?.permissions?.viewIssues)}
						schemaConfiguration={schema?.configuration}
						renderer={f.renderer}
						accessLevel={fg('jira-forms-public-access') ? accessLevel : undefined}
					/>
				);

				return f.parentCondition && fg('jira_forms_conditional_field_logic_gate_') ? (
					<Condition key={`${f.parentCondition.id}:${f.fieldId}`} condition={f.parentCondition}>
						{field}
					</Condition>
				) : (
					field
				);
			});

			return renderedFields;
		},
		[
			fieldMetaData,
			projectData?.type,
			projectData?.key,
			projectData?.permissions?.viewIssues,
			uploadPermissions,
			mediaUploadPermissions,
			projectId,
			issueType?.id,
			formId,
			createAnalyticsEvent,
			accessLevel,
		],
	);

	const getErrorFlag = useCallback(
		() =>
			errorMessage !== null && typeof errorMessage === 'string'
				? submissionErrorFlag(errorMessage)
				: errorFlags.genericError(),
		[errorMessage],
	);

	useEffect(() => {
		if (typeof document !== 'undefined') {
			interactions.forEach((evt) =>
				document.addEventListener(evt, renderExtraFields, hasInitialInteractionCapture),
			);
		}
		return () => {
			if (typeof document !== 'undefined') {
				interactions.forEach((evt) =>
					document.removeEventListener(evt, renderExtraFields, hasInitialInteractionCapture),
				);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (
			typeof projectId === 'number' &&
			(!fg('fix_attachments_for_description_on_forms') || !isPublicForm)
		) {
			fetchUploadPermissions(String(projectId), formId);
		}
	}, [fetchUploadPermissions, formId, projectId, isPublicForm]);

	if (createIssueData?.success && !isVisible) {
		return (
			<div data-testid="business-form.ui.form-submission.fill-in-view.success-view">
				<SuccessView
					shouldHidePoweredBy={shouldHidePoweredBy}
					onSubmitAnotherFormClick={handleResetClick}
					analyticAttributes={analyticAttributes}
					{...(fg('jira-forms-public-access') && {
						issueKey: createIssueData?.issueKey,
					})}
				/>
			</div>
		);
	}

	if (!formSubmissionData || !formWithFieldData || !fieldMetaData || isUploadPermissionNotReady) {
		return null;
	}

	// only render the first 15 fields for performance reasons
	// remaining fields will be rendered once the user interacts with the page
	const cloneInitialFields = [...formWithFieldData.fields];
	const initialFields = cloneInitialFields.splice(0, 15);
	const shouldRenderAdditionalFields = cloneInitialFields.length > 0 && shouldRenderExtraFields;

	return (
		<ContextualAnalyticsData
			objectType="form"
			objectId={formId}
			containerType="project"
			containerId={String(projectId)}
			attributes={{
				...generateFieldAnalytics(formWithFieldData.fields),
				...analyticAttributes,
			}}
		>
			<FireTrackAnalytics eventName="formSubmission viewed" attributes={analyticAttributes} />
			{fg('jira-forms-public-access') ? (
				<>
					{projectData?.name && (
						<Box xcss={projectNameStyles}>
							<Inline alignBlock="center" space="space.100">
								<Avatar appearance="square" size="small" src={projectData?.avatarUrl} />
								<Text as="span" color="color.text.subtle" weight="semibold">
									{projectData.name}
								</Text>
							</Inline>
						</Box>
					)}
					{/* This placeholder is to prevent layout shifts when loading the project name. */}
					{/* However, the project name is not displayed in public forms. */}
					{!projectData?.name && !isPublicForm && <Box xcss={projectNamePlaceholderStyles} />}
				</>
			) : (
				<>
					{projectData?.name ? (
						<ProjectNameWrapper>
							<Inline alignBlock="center" space="space.100">
								<Avatar appearance="square" size="small" src={projectData?.avatarUrl} />
								<Text as="span" color="color.text.subtle" weight="semibold">
									{projectData?.name}
								</Text>
							</Inline>
						</ProjectNameWrapper>
					) : (
						<Box xcss={projectNamePlaceholderStyles} />
					)}
				</>
			)}
			<Tooltip
				content={formWithFieldData.title}
				position={fg('jira-forms-public-access') ? 'bottom-start' : 'right'}
			>
				<TitleWrapperTruncated>{formWithFieldData.title}</TitleWrapperTruncated>
			</Tooltip>
			<DescriptionWrapper data-testid="business-form.ui.form-submission.fill-in-view.description">
				{parsedDescription ? (
					<Placeholder name="description-view" fallback={null}>
						<AsyncDescriptionReadView
							document={parsedDescription}
							mediaContext={mediaContext}
							featureFlags={{
								codeBidiWarnings: true,
								'code-bidi-warnings': true,
							}}
							// eslint-disable-next-line jira/deprecations/no-base-url
							baseUrl=""
						/>
					</Placeholder>
				) : null}
			</DescriptionWrapper>
			<Form
				isDisabled={createIssueLoading || (createIssueData?.success && !isVisible)}
				onSubmit={isPublicForm ? handlePublicFormSubmitClick : handleInternalFormSubmitClick}
			>
				{({ formProps }) => (
					<form
						{...formProps}
						name="submit form"
						noValidate
						data-test-id="business-form.ui.form-submission.fill-in-view.form"
					>
						{fg('jira-forms-public-access') && (
							<Fields
								fields={
									shouldRenderExtraFields
										? formWithFieldData.fields
										: formWithFieldData.fields.slice(0, 15)
								}
								setUploading={setUploading}
								showEmailField={isPublicForm && emailRequired}
								{...(fg('fix_attachments_for_description_on_forms') && {
									uploadPermissions,
								})}
							/>
						)}
						{!fg('jira-forms-public-access') && renderFields(initialFields)}
						{shouldRenderAdditionalFields &&
							!fg('jira-forms-public-access') &&
							renderFields(cloneInitialFields)}
						{!shouldRenderAdditionalFields && <span ref={setIntersectionRef} />}
						{(shouldRenderAdditionalFields || formWithFieldData.fields.length <= 15) && (
							<Footer
								isPublicForm={isPublicForm}
								createIssueLoading={createIssueLoading}
								uploading={uploading}
								onRecaptchaChange={setRecaptcha}
								onCheckboxRecaptchaVerify={(recaptchaToken) => {
									setCheckboxRecaptchaToken(recaptchaToken);
									setIsCheckboxRecaptchaNotVerifiedOnSubmit(false);
								}}
								onCheckboxRecaptchaExpire={() => setCheckboxRecaptchaToken(null)}
								shouldShowCheckboxRecaptcha={isInvisibleRecaptchaFailed}
								checkboxRecaptchaNotVerified={isCheckboxRecaptchaNotVerifiedOnSubmit}
							/>
						)}
						{fg('jira_forms_conditional_field_logic_gate_') && <ScreenreaderAnnouncer />}
					</form>
				)}
			</Form>
			{(createIssueError || isRecaptchaError) && (
				<div data-testid="business-form.ui.form-submission.fill-in-view.flag">
					<FlagRenderer flag={getErrorFlag()} />
				</div>
			)}
		</ContextualAnalyticsData>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProjectNameWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.200'),
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: 0,
});

const projectNameStyles = xcss({
	marginBottom: 'space.200',
});

const projectNamePlaceholderStyles = xcss({
	marginTop: 'space.200',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DescriptionWrapper = styled.div({
	marginTop: token('space.100'),
});
